import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, studentAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  ArrowLeft,
  Banknote,
  Shield,
  Clock,
  Users,
  MessageCircle,
  AlertTriangle,
  CheckCircle,
  Loader2,
  User,
  Copy,
  Mail,
  Phone,
  Send,
  FileText,
  ShieldAlert
} from 'lucide-react';

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const packageData = location.state?.packageData;
  const selectedSubjects = location.state?.selectedSubjects || [];
  const selectedTutor = location.state?.selectedTutor;
  
  const [isUnder18, setIsUnder18] = useState(false);
  const [consentStep, setConsentStep] = useState('check'); // 'check', 'consent', 'payment', 'success', 'confirmed', 'blocked'
  const [parentName, setParentName] = useState('');
  const [parentConsent, setParentConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingSent, setConfirmingSent] = useState(false);
  const [orderId, setOrderId] = useState('');
  
  // Eligibility state
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [eligibilityIssues, setEligibilityIssues] = useState([]);

  // E-transfer details
  const etransferEmail = 'contact@giftedacademytutors.ca';

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please sign in to continue with checkout');
      navigate('/login', { state: { from: '/checkout', packageData, selectedSubjects } });
      return;
    }
    
    if (!packageData) {
      toast.error('No package selected');
      navigate('/pricing');
      return;
    }

    // Check booking eligibility first
    const checkEligibility = async () => {
      try {
        const response = await studentAPI.getBookingEligibility();
        const data = response.data;
        
        if (!data.eligible) {
          setEligibilityIssues(data.issues || []);
          setConsentStep('blocked');
          toast.error('Please complete required steps before checkout');
        } else {
          // User is eligible - proceed with normal flow
          const userAge = user?.age;
          if (userAge && userAge < 18) {
            setIsUnder18(true);
            if (user?.consent_status === 'approved' && user?.parent_name) {
              setParentName(user.parent_name);
              setParentConsent(true);
              setConsentStep('payment');
            } else {
              setConsentStep('consent');
            }
          } else {
            setIsUnder18(false);
            setConsentStep('payment');
          }
        }
      } catch (error) {
        console.error('Error checking eligibility:', error);
        // If eligibility check fails, still proceed but with warning
        const userAge = user?.age;
        if (userAge && userAge < 18) {
          setIsUnder18(true);
          setConsentStep('consent');
        } else {
          setConsentStep('payment');
        }
      } finally {
        setEligibilityChecked(true);
      }
    };
    
    checkEligibility();
  }, [isAuthenticated, packageData, user, navigate, selectedSubjects]);

  const handleProceedWithConsent = () => {
    if (!parentName.trim()) {
      toast.error('Please enter parent/guardian name');
      return;
    }
    if (!parentConsent) {
      toast.error('Please confirm parent/guardian consent');
      return;
    }
    setConsentStep('payment');
  };

  const handleConfirmOrder = async () => {
    setSubmitting(true);
    
    try {
      const response = await ordersAPI.createEtransfer({
        package_type: packageData.studentLevel,
        hours: packageData.hours,
        amount: packageData.totalCost,
        subjects: selectedSubjects,
        student_name: user?.name,
        student_email: user?.email,
        parent_guardian_name: isUnder18 ? parentName : null,
        parent_consent_given: isUnder18 ? parentConsent : null,
        preferred_tutor_id: selectedTutor?.profile_id || selectedTutor?.user_id || null,
        preferred_tutor_name: selectedTutor?.name || null
      });
      
      setOrderId(response.data.order_id);
      setConsentStep('success');
      toast.success('Order submitted! Please complete e-transfer to confirm.');
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmEtransferSent = async () => {
    setConfirmingSent(true);
    try {
      await ordersAPI.confirmSent(orderId);
      setConsentStep('confirmed');
      toast.success('Thank you! We will verify your payment shortly.');
    } catch (error) {
      console.error('Confirm error:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    } finally {
      setConfirmingSent(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (!packageData) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-700" />
      </div>
    );
  }

  // Blocked state - show eligibility issues
  if (consentStep === 'blocked') {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="checkout-blocked">
        {/* Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
          </div>
        </nav>

        <main className="pt-24 pb-12 px-6">
          <div className="max-w-xl mx-auto">
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <ShieldAlert className="h-8 w-8 text-amber-600" />
                </div>
                <CardTitle className="font-heading text-xl text-amber-900">
                  Complete Required Steps First
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-center text-amber-800">
                  Before you can proceed with checkout, please complete the following requirements:
                </p>
                
                <div className="space-y-3">
                  {eligibilityIssues.map((issue, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-start gap-3 p-4 bg-white rounded-xl border border-amber-200"
                      data-testid={`eligibility-issue-${issue.type}`}
                    >
                      {issue.type === 'email_not_verified' && (
                        <>
                          <Mail className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-amber-900">{issue.message}</p>
                            <Button 
                              size="sm"
                              variant="outline"
                              className="mt-2 rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                              onClick={() => navigate('/verify-email')}
                              data-testid="verify-email-btn"
                            >
                              Verify Email
                            </Button>
                          </div>
                        </>
                      )}
                      {(issue.type === 'agreement_required' || issue.type === 'consent_required') && (
                        <>
                          <FileText className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-amber-900">{issue.message}</p>
                            <Button 
                              size="sm"
                              variant="outline"
                              className="mt-2 rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                              onClick={() => navigate('/student/agreement')}
                              data-testid="complete-agreement-btn"
                            >
                              {issue.type === 'consent_required' ? 'Complete Parent Consent' : 'Sign Agreement'}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-3 pt-4">
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="w-full rounded-full"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={() => navigate('/pricing')}
                    className="w-full rounded-full"
                  >
                    Back to Pricing
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="checkout-page">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-indigo-700" />
              <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                Gifted Academy Tutors
              </span>
            </Link>
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            {/* Order Summary - Left Side */}
            <div className="md:col-span-2">
              <Card className="border-stone-200 sticky top-24">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Package</span>
                      <span className="font-medium">
                        {packageData.studentLevel === 'highSchool' ? 'High School' : 'University'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Hours</span>
                      <span className="font-medium">{packageData.hours} hours</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Rate</span>
                      <span className="font-medium">${packageData.rate}/hour</span>
                    </div>
                    {packageData.discount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount</span>
                        <span>-{packageData.discount}%</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Subjects</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedSubjects.map((subject, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full"
                        >
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Preferred Tutor */}
                  {selectedTutor && (
                    <div className="border-t pt-3">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Preferred Tutor</span>
                      </div>
                      <div className="flex items-center gap-2 bg-indigo-50 rounded-lg p-2">
                        <div className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center text-indigo-700 font-medium text-sm">
                          {selectedTutor.name?.charAt(0)}
                        </div>
                        <span className="font-medium text-sm text-indigo-900">{selectedTutor.name}</span>
                      </div>
                    </div>
                  )}

                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-900">Total</span>
                      <span className="text-2xl font-bold text-indigo-700">
                        ${packageData.totalCost.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Trust badges */}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Shield className="h-4 w-4 text-green-600" />
                      <span>Secure e-transfer payment</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock className="h-4 w-4 text-blue-600" />
                      <span>Sessions confirmed within 24 hours</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Users className="h-4 w-4 text-purple-600" />
                      <span>Matched with expert tutors</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Checkout Steps - Right Side */}
            <div className="md:col-span-3">
              <AnimatePresence mode="wait">
                {/* Consent Step for Under-18 */}
                {consentStep === 'consent' && (
                  <motion.div
                    key="consent"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <Card className="border-amber-200 bg-amber-50/50">
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <AlertTriangle className="h-5 w-5 text-amber-700" />
                          </div>
                          <div>
                            <CardTitle className="font-heading text-xl text-amber-900">
                              Parent/Guardian Consent Required
                            </CardTitle>
                            <p className="text-sm text-amber-700 mt-1">
                              Students under 18 require parental consent
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="parentName">Parent/Guardian Full Name</Label>
                            <Input
                              id="parentName"
                              value={parentName}
                              onChange={(e) => setParentName(e.target.value)}
                              placeholder="Enter parent/guardian name"
                              className="bg-white"
                              data-testid="parent-name-input"
                            />
                          </div>

                          <div className="flex items-start space-x-3">
                            <Checkbox
                              id="parentConsent"
                              checked={parentConsent}
                              onCheckedChange={setParentConsent}
                              data-testid="parent-consent-checkbox"
                            />
                            <div className="space-y-1">
                              <Label htmlFor="parentConsent" className="text-sm leading-relaxed">
                                I confirm that I am the parent/guardian of the student and I consent to this tutoring service purchase.
                              </Label>
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={handleProceedWithConsent}
                          disabled={!parentName.trim() || !parentConsent}
                          className="w-full rounded-full bg-amber-600 hover:bg-amber-700"
                          data-testid="proceed-consent-btn"
                        >
                          Proceed to Payment
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Payment Step - E-Transfer */}
                {consentStep === 'payment' && (
                  <motion.div
                    key="payment"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <Card className="border-stone-200">
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <Banknote className="h-5 w-5 text-green-700" />
                          </div>
                          <div>
                            <CardTitle className="font-heading text-xl">
                              Pay via Interac e-Transfer
                            </CardTitle>
                            <p className="text-sm text-slate-500 mt-1">
                              Simple and secure Canadian payment
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Show consent confirmation for under-18 */}
                        {isUnder18 && parentName && (
                          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-green-800">
                                Parent/Guardian Consent Confirmed
                              </p>
                              <p className="text-xs text-green-700 mt-1">
                                Consenting guardian: {parentName}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* User info display */}
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-slate-700">Purchasing as:</p>
                          <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-indigo-700" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{user?.name}</p>
                              <p className="text-sm text-slate-500">{user?.email}</p>
                            </div>
                          </div>
                        </div>

                        {/* E-Transfer Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                          <h4 className="font-medium text-blue-900">E-Transfer Instructions:</h4>
                          <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                            <li>Click "Confirm Order" below to submit your order</li>
                            <li>Send an Interac e-Transfer for <strong>${packageData.totalCost}</strong></li>
                            <li>Use your name and email as the message</li>
                            <li>We'll confirm your order within 24 hours</li>
                          </ol>
                          
                          <div className="pt-2 border-t border-blue-200">
                            <p className="text-sm text-blue-700 mb-2">Send e-Transfer to:</p>
                            <div 
                              className="flex items-center justify-between bg-white rounded-lg p-3 cursor-pointer hover:bg-blue-50 transition-colors"
                              onClick={() => copyToClipboard(etransferEmail)}
                            >
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-blue-600" />
                                <span className="font-mono text-blue-900">{etransferEmail}</span>
                              </div>
                              <Copy className="h-4 w-4 text-blue-500" />
                            </div>
                          </div>
                        </div>

                        {/* Confirm Order button */}
                        <Button
                          onClick={handleConfirmOrder}
                          disabled={submitting}
                          className="w-full rounded-full bg-green-600 hover:bg-green-700 h-12 text-base"
                          data-testid="confirm-order-btn"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Submitting Order...
                            </>
                          ) : (
                            <>
                              <Banknote className="h-5 w-5 mr-2" />
                              Confirm Order - ${packageData.totalCost.toLocaleString()}
                            </>
                          )}
                        </Button>

                        {/* Contact option */}
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-stone-200" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-slate-500">or</span>
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          onClick={() => navigate('/contact')}
                          className="w-full rounded-full"
                          data-testid="contact-admin-btn"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Contact Us with Questions
                        </Button>

                        <p className="text-xs text-center text-slate-500">
                          E-Transfer is secure and instant. Your sessions will be confirmed once payment is received.
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Success Step */}
                {consentStep === 'success' && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <Card className="border-amber-200 bg-amber-50/50">
                      <CardContent className="pt-8 pb-8 text-center">
                        <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                          <Banknote className="h-10 w-10 text-amber-600" />
                        </div>
                        <h2 className="font-heading text-2xl font-semibold text-amber-900 mb-2">
                          Order Submitted - Awaiting Payment
                        </h2>
                        <p className="text-amber-700 mb-6">
                          Please send your e-transfer and then click the button below to confirm.
                        </p>
                        
                        {orderId && (
                          <div className="bg-white rounded-lg p-4 mb-6 text-left max-w-sm mx-auto border border-amber-200">
                            <div className="text-sm text-slate-600 mb-2">Your Order Reference:</div>
                            <div 
                              className="font-mono text-xl font-bold text-slate-900 cursor-pointer flex items-center justify-between"
                              onClick={() => copyToClipboard(orderId)}
                            >
                              {orderId}
                              <Copy className="h-4 w-4 text-slate-400" />
                            </div>
                            <p className="text-xs text-slate-500 mt-2">Include this reference in your e-transfer message</p>
                          </div>
                        )}

                        <div className="bg-white rounded-xl p-4 mb-6 text-left max-w-sm mx-auto border border-amber-200">
                          <h4 className="font-medium text-slate-900 mb-3">Send e-Transfer to:</h4>
                          <div 
                            className="flex items-center justify-between bg-amber-50 rounded-lg p-3 cursor-pointer hover:bg-amber-100 transition-colors"
                            onClick={() => copyToClipboard(etransferEmail)}
                          >
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-amber-600" />
                              <span className="font-mono text-amber-900">{etransferEmail}</span>
                            </div>
                            <Copy className="h-4 w-4 text-amber-500" />
                          </div>
                          <p className="text-sm text-slate-600 mt-3">
                            Amount: <strong>${packageData.totalCost}</strong>
                          </p>
                        </div>

                        <div className="flex flex-col gap-3 max-w-sm mx-auto">
                          <Button
                            onClick={handleConfirmEtransferSent}
                            disabled={confirmingSent}
                            className="rounded-full bg-green-600 hover:bg-green-700 h-12"
                            data-testid="confirm-sent-btn"
                          >
                            {confirmingSent ? (
                              <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Confirming...
                              </>
                            ) : (
                              <>
                                <Send className="h-5 w-5 mr-2" />
                                I've Sent the E-Transfer
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate('/dashboard')}
                            className="rounded-full"
                          >
                            I'll Do This Later
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Confirmed Step */}
                {consentStep === 'confirmed' && (
                  <motion.div
                    key="confirmed"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <Card className="border-green-200 bg-green-50/50">
                      <CardContent className="pt-8 pb-8 text-center">
                        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="h-10 w-10 text-green-600" />
                        </div>
                        <h2 className="font-heading text-2xl font-semibold text-green-900 mb-2">
                          Payment Confirmation Received!
                        </h2>
                        <p className="text-green-700 mb-6">
                          Thank you! We'll verify your e-transfer and activate your sessions within 24 hours.
                        </p>
                        
                        {orderId && (
                          <div className="bg-white rounded-lg p-4 mb-6 text-left max-w-sm mx-auto">
                            <div className="text-sm text-slate-600 mb-2">Order Reference:</div>
                            <div className="font-mono text-lg font-semibold text-slate-900">{orderId}</div>
                            <p className="text-xs text-green-600 mt-2">Awaiting admin verification</p>
                          </div>
                        )}

                        <div className="flex flex-col gap-3 max-w-sm mx-auto">
                          <Button
                            onClick={() => navigate('/dashboard')}
                            className="rounded-full bg-green-600 hover:bg-green-700"
                            data-testid="go-to-dashboard-btn"
                          >
                            Go to Dashboard
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate('/contact')}
                            className="rounded-full"
                          >
                            <Phone className="h-4 w-4 mr-2" />
                            Need Help?
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;
